'use client';

import { useEffect, useState } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Button from '@mui/material/Button';
import { WishlistPostRequest } from '@/types/gearApi/Wishlist';
import WishlistUpdateType from '@/enums/WishlistType';
import ActionButton from '../ActionButton';
import UnfavoriteIcon from './components/UnfavoriteIcon';
import { postToWishlists } from './actions';

export interface FavoriteButtonProps {
	initialState?: boolean;
	sku: string;
	size?: number;
	buttonText?: string;
	favoriteColor?: 'primary' | 'secondary' | 'accent' | 'lightAccent';
	unfavoriteColor?: 'primary' | 'secondary' | 'accent' | 'lightAccent';
}

const FavoriteButton = ({
	initialState = false,
	sku,
	size = 24,
	buttonText,
	favoriteColor = 'primary',
	unfavoriteColor = 'primary',
}: FavoriteButtonProps) => {
	const [isFavorite, setIsFavorite] = useState(initialState);

	const formattedSize = buttonText ? 18 : size;

	const icon = isFavorite ? (
		<FavoriteIcon
			color={favoriteColor}
			sx={{ width: formattedSize, height: formattedSize }}
			data-testid="favorite-icon"
		/>
	) : (
		<UnfavoriteIcon color={unfavoriteColor} width={formattedSize} height={formattedSize} data-testid="unfavorite-icon" />
	);

	const handleClick = async () => {
		const nextIsFavorite = !isFavorite;
		setIsFavorite(nextIsFavorite);

		const postWishlistRequest: WishlistPostRequest = {
			skus: [sku],
			updateType: nextIsFavorite ? WishlistUpdateType.ADD_PRODUCT : WishlistUpdateType.REMOVE_PRODUCT,
		};

		const { success } = await postToWishlists(postWishlistRequest);
		if (!success) setIsFavorite(!nextIsFavorite);
	};

	useEffect(() => {
		setIsFavorite(initialState);
	}, [sku, initialState]);

	return buttonText ? (
		<Button variant="text" onClick={handleClick} startIcon={icon}>
			{buttonText}
		</Button>
	) : (
		<ActionButton
			onClick={handleClick}
			icon={icon}
			sx={{ border: 0, padding: 0, '&:hover': { backgroundColor: 'transparent' } }}
		/>
	);
};

export default FavoriteButton;
